// src/components/Customer/Payment.js
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CreditCard, Apple } from "lucide-react";
import { auth, db } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useToast } from "../../contexts/ToastContext";

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export default function Payment() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { showToast } = useToast();   // global

  const [docId, setDocId] = useState(null);
  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState("mada");
  const [processing, setProcessing] = useState(false);

  const paymentMethods = [
    { id: "mada", name: "mada", icon: "💳" },
    { id: "stc", name: "STC Pay", icon: "📱" },
    { id: "apple", name: "Apple Pay", icon: <Apple size={24} /> },
    { id: "card", name: "Credit Card", icon: <CreditCard size={24} /> },
  ];

  // ✅ Load only from jobs
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        if (!jobId) throw new Error("Missing jobId");

        const snap = await getDoc(doc(db, "jobs", jobId));
        if (!snap.exists()) throw new Error("Job not found");

        const d = snap.data();

        // 🔥 Redirect if already paid
        if (d.paymentStatus === "paid") {
          navigate(`/customer/order-status/${jobId}`, { replace: true });
          return;
        }

        if (!cancelled) {
          setDocId(jobId);
          setData(d);
        }
      } catch (e) {
        console.error("Load payment error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, navigate]);

  const total = safeNumber(data?.finalPrice ?? data?.price ?? data?.budget, 0);

  // ✅ Handle Payment
  const handlePayment = async () => {
    const user = auth.currentUser;
    if (!user || !docId || processing || !data) return;

    setProcessing(true);



    try {
      await new Promise((res) => setTimeout(res, 1000));

      const baseUpdate = {
        paymentStatus: "paid",
        paymentMethod: selectedMethod,
        updatedAt: serverTimestamp(),
      };

      // Direct booking flow
      if (data.status === "draft") {
        await updateDoc(doc(db, "jobs", docId), {
          ...baseUpdate,
          status: "open",
        });
        showToast("Payment handled successfully!");
        navigate(`/customer/order-status/${docId}`);
        return;
      }

      // Accepted offer flow
      if (data.status === "pending_payment") {
        await updateDoc(doc(db, "jobs", docId), {
          ...baseUpdate,
          status: "assigned",
          progressStatus: "confirmed",
        });

        showToast("Payment handled successfully!!");
        navigate(`/customer/order-status/${docId}`);
        return;
      }

      // Fallback for unexpected states
      await updateDoc(doc(db, "jobs", docId), {
        ...baseUpdate,
      });

      showToast("Payment handled successfully!");
      navigate(`/customer/order-status/${docId}`);
    } catch (e) {
      console.error("Payment error:", e);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading payment...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Payment</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg p-6 shadow-sm mb-6 text-center">
          <div className="text-gray-600 mb-2">Total Amount</div>
          <div className="text-[#006C35] text-2xl font-semibold">
            {total} SAR
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
          <h3 className="text-[#1E3A8A] mb-4">Select Payment Method</h3>
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                  selectedMethod === method.id
                    ? "border-[#006C35] bg-green-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  {typeof method.icon === "string" ? method.icon : method.icon}
                </div>
                <span className="flex-1 text-left text-[#1E3A8A]">
                  {method.name}
                </span>
                {selectedMethod === method.id && (
                  <div className="w-6 h-6 bg-[#006C35] rounded-full flex items-center justify-center text-white text-sm">
                    ✓
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handlePayment}
          disabled={processing}
          className="w-full bg-[#006C35] text-white py-4 rounded-lg hover:bg-[#005428] transition-colors disabled:opacity-60"
        >
          🔒 {processing ? "Processing..." : "Pay Securely"}
        </button>
      </div>
    </div>
  );
}